/**
 * Theme provider: 'system' | 'light' | 'dark'.
 *
 * Flow:
 *   1. Initial paint reads localStorage('mileageiq.theme') BEFORE React mounts
 *      (handled by the inline boot script in `public/index.html`) — no flash.
 *   2. The provider rehydrates that value into state, applies `data-theme` to
 *      <html>, and listens to `prefers-color-scheme` when mode='system'.
 *   3. When the user signs in, server-side SettingsAPI.get() supplies their
 *      authoritative preference and we reconcile (server wins on conflict).
 *   4. setMode() persists locally and PATCHes settings so other devices sync.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { SettingsAPI } from "@/lib/api";

const STORAGE_KEY = "mileageiq.theme";
const VALID = ["system", "light", "dark"];

const ThemeCtx = createContext({
  mode: "system",
  resolvedTheme: "dark",
  setMode: () => {},
});

const readStoredMode = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch {
    return "system";
  }
};

const detectSystem = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
};

const resolve = (mode) => (mode === "system" ? detectSystem() : mode);

const applyDom = (resolved) => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
};

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolve(readStoredMode()));

  // Mirror mode → DOM whenever it changes (initial mount included).
  useEffect(() => {
    const next = resolve(mode);
    setResolvedTheme(next);
    applyDom(next);
  }, [mode]);

  // Subscribe to OS preference changes while in 'system' mode.
  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => {
      const next = mq.matches ? "light" : "dark";
      setResolvedTheme(next);
      applyDom(next);
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode]);

  // Pull authoritative preference from server settings once the user is
  // logged in (SettingsAPI 401s gracefully when anonymous — caught silently).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await SettingsAPI.get();
        if (cancelled) return;
        const serverMode = s?.theme_mode;
        if (VALID.includes(serverMode) && serverMode !== mode) {
          try { localStorage.setItem(STORAGE_KEY, serverMode); } catch { /* noop */ }
          setModeState(serverMode);
        }
      } catch { /* anonymous or transient — ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMode = useCallback(async (next) => {
    if (!VALID.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    setModeState(next);
    // Best-effort server sync.
    try { await SettingsAPI.update({ theme_mode: next }); } catch { /* offline or anon */ }
  }, []);

  return (
    <ThemeCtx.Provider value={{ mode, resolvedTheme, setMode }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);

/**
 * Snapshot of resolved chart colors. Recharts SVG attributes (`stroke`,
 * `fill`, `tick.fill`) don't honor `var(--…)` references reliably — they're
 * applied as XML presentation attrs, not as inline CSS. So we materialize
 * the current values from `getComputedStyle(document.documentElement)`
 * whenever the resolved theme flips.
 */
const TOKENS = [
  "text-primary",
  "text-secondary",
  "text-muted",
  "text-faint",
  "border-subtle",
  "border-hover",
  "border-strong",
  "bg-elevated",
  "bg-hover",
  "bg-inverse",
  "text-inverse",
];

export function useThemeColors() {
  const { resolvedTheme } = useTheme();
  return useMemo(() => {
    if (typeof window === "undefined") return {};
    const cs = getComputedStyle(document.documentElement);
    const out = {};
    for (const t of TOKENS) {
      const v = cs.getPropertyValue(`--${t}`).trim();
      out[t] = v || "#000000";
    }
    return out;
     
  }, [resolvedTheme]);
}

/**
 * Force a sub-tree to a fixed theme (e.g. the landing page stays dark
 * regardless of the user's choice — it's a brand surface). Wrap the section
 * in <ForceTheme value="dark">…</ForceTheme>.
 */
export function ForceTheme({ value, children }) {
  return (
    <div data-theme={value} style={{ display: "contents" }}>
      {children}
    </div>
  );
}
